import React from 'react';

// FilterGrid — the standardized layout for the inside of a <FiltersCard>.
//
// Why this exists. Filter bodies used to be laid out with <Row>/<Col md={2}> etc. `Col` maps a
// numeric size to a FIXED fraction (`md:tw-w-2/12`), so six filters on a 768 px tablet each get
// 128 px whether or not their label fits — labels and select values truncate, and the row cannot
// reflow because every track width is pinned. That is the single cause behind the "filters are
// truncated and do not expand even when there is space" report on Routine, Projects, Work IDs,
// PCR, Submissions and the Project Summary report.
//
// The fix is to stop declaring column counts at all. `auto-fit` + `minmax(<min>, 1fr)` lets the
// browser fit as many tracks as will hold a control at its minimum readable width and wrap the
// rest — so the same markup is a single column on a phone, three on a tablet and six on a wide
// desktop, with no breakpoints to keep in sync per page.
//
// Usage:
//   <FiltersCard title="Filters" ...>
//     <FilterGrid>
//       <Form.Group>…</Form.Group>            {/* one track */}
//       <FilterField span={2}>…</FilterField> {/* two tracks where there is room */}
//     </FilterGrid>
//   </FiltersCard>

/** Minimum readable width for one control before the grid drops a track. */
const MIN_TRACK = {
  sm: 'tw-grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]',
  md: 'tw-grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]',
  lg: 'tw-grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]',
} as const;

export interface FilterGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Narrowest a single filter may get before the grid wraps instead of shrinking it.
   * `md` (11rem) suits a label over a select; use `lg` for date ranges and search inputs.
   */
  min?: keyof typeof MIN_TRACK;
}

export const FilterGrid: React.FC<FilterGridProps> = ({
  min = 'md',
  className = '',
  children,
  ...rest
}) => (
  // items-end so a control with a two-line label still baselines with its neighbours — the
  // "work date filters are misaligned" report on the Submissions page.
  // `wrap-filter-grid` (tailwind.css) gives every control in the row ONE look - native selects,
  // date inputs, MultiSelectDropdown, SearchableDropdown and StandoutDropdown all get the same
  // height, border, radius and font size - and every label the same size, line height and gap,
  // so labels share a baseline (W-M1).
  <div
    className={`wrap-filter-grid tw-grid tw-items-end tw-gap-x-3 tw-gap-y-3 ${MIN_TRACK[min]} ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export interface FilterFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Tracks to occupy where the viewport has room. Always full-width on a phone (where the grid
   * is one track anyway), so a wide field never forces a horizontal scroll.
   */
  span?: 1 | 2 | 3;
}

const SPAN = {
  1: '',
  2: 'tw-col-span-full sm:tw-col-span-2',
  3: 'tw-col-span-full sm:tw-col-span-2 lg:tw-col-span-3',
} as const;

export const FilterField: React.FC<FilterFieldProps> = ({
  span = 1,
  className = '',
  children,
  ...rest
}) => (
  // min-w-0 is what actually lets a select shrink inside its track instead of overflowing it.
  <div className={`tw-min-w-0 ${SPAN[span]} ${className}`} {...rest}>
    {children}
  </div>
);

export default FilterGrid;
